import { useMemo, useState } from 'react';

import { useEmailSenders } from '@/hooks/useCompanySettings';

export interface FromOption {
  value: string;
  label: string;
}

// From-address selection shared by the email steps: the RM's own email plus the
// configured Email Senders.
//
// DEFAULTS TO THE EMPLOYEE'S OWN ADDRESS, not the department one. A client
// replying to a quotation should reach the person who sent it; a shared
// department inbox as the default meant every reply landed in one queue with
// nobody named on it. The configured department senders remain in the list,
// below the RM's own address, so an RM whose address has a delivery problem can
// still fall back to one.
//
// TRADE-OFF, accepted deliberately: a department sender is on a domain verified
// with the email provider, and an individual employee address may not be - so a
// send from an unverified address can fail SPF/DKIM alignment and land in spam.
// The fallback in the dropdown is the mitigation.
export const useFromSelector = (rmEmail: string) => {
  const { data: senderOptions = [] } = useEmailSenders();

  const fromOptions = useMemo<FromOption[]>(() => {
    const opts = senderOptions.map((s) => ({ value: s.email, label: `${s.label} (${s.email})` }));
    if (rmEmail && !senderOptions.some((s) => s.email.toLowerCase() === rmEmail.toLowerCase())) {
      // Their own address goes FIRST: it is the default, and a default sitting
      // at the bottom of a list reads as an afterthought.
      opts.unshift({ value: rmEmail, label: `My email (${rmEmail})` });
    }
    return opts;
  }, [senderOptions, rmEmail]);

  // Falls back to a configured sender only when the employee has no email on
  // their profile - resolveFromIdentity on the server applies the same order.
  const defaultFromEmail = useMemo(() => {
    if (rmEmail) return rmEmail;
    const sales = senderOptions.find((s) => s.label.toLowerCase().includes('sales'));
    return sales?.email ?? senderOptions[0]?.email ?? '';
  }, [senderOptions, rmEmail]);

  const [fromChoice, setFromChoice] = useState('');
  const fromEmail = fromChoice || defaultFromEmail;

  return { fromOptions, fromEmail, setFromChoice };
};
